import { useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ButtonSecondary } from '@components/button';
import { CardDisclosure } from '@components/card-disclosure';
import { CRITICAL } from '@components/card-disclosure/src/card-disclosure.type';
import { createRowErrors } from '@components/forms/utils/create-row-errors';
import { IconCheckCircle, IconLoading } from '@components/icons';
import { ProgressLine } from '@components/progress-line';
import { EVENT_LIST } from '@constants/routes';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { LAYOUT_WITHOUT_SIDEBAR } from '@contexts/layout/layout.type';
import { LayoutContext } from '@contexts/layout/layout-context';
import { NavigationContext } from '@contexts/navigation/navigation-context';
import { useGetEvent } from '@hooks/events/use-get-event';
import { useGetImportStatus } from '@hooks/import/use-get-import-status';
import { VIEW_UPCOMING } from '@hooks/use-event-navigation/use-event-navigation';
import { calculatePercentage } from '@utils/calculate-percentage';

const ImportProcess = () => {
  const { eventId, importId, importType } = useParams<{ eventId: string; importId: string; importType: string }>();
  const { data: eventData } = useGetEvent({ eventId, options: { enabled: !!eventId } });
  const [isLoading, setIsLoading] = useState(true);
  const {
    route: { from: backRoute, to: toRoute },
  } = useContext(NavigationContext);
  const { setLayout } = useContext(LayoutContext);
  const {
    clubState: { slug: clubSlug },
    companyState: { legacyId: companyLegacyId },
  } = useContext(CompanyClubContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: importCsvStatusData } = useGetImportStatus({
    importId,
    options: { enabled: isLoading, refetchInterval: 1000 },
  });

  const createEventListPath = () => {
    if (!eventId) return;
    const path = EVENT_LIST.replace(':companyId', companyLegacyId)
      .replace(':clubSlug', clubSlug)
      .replace(':eventId', eventId)
      .replace(':view', VIEW_UPCOMING);
    return path;
  };
  const navigateBack = () => {
    if (backRoute !== toRoute) {
      navigate(backRoute);
      return;
    }
    const path = createEventListPath();
    if (path) navigate(path);
  };

  const failedRowsMessage = importCsvStatusData?.failed?.quantity ? t('common:processImportInfoWithErrors') : undefined;

  const failedRowsMultiInviteMessage = importCsvStatusData?.failed?.quantity
    ? t('common:processImportMultiInfoWithErrors')
    : undefined;

  const failedRowsOfflineSaleMessage = importCsvStatusData?.failed?.quantity
    ? t('common:processImportMultiInfoWithErrors')
    : undefined;

  const failedRowsPassHolderMessage = importCsvStatusData?.failed?.quantity
    ? t('common:processImportPassHolderInfoWithErrors')
    : undefined;

  const totalRowsProcessed = importCsvStatusData
    ? importCsvStatusData?.pending + importCsvStatusData?.completed + importCsvStatusData?.failed.quantity
    : 0;

  const loadingImportCsvMessage = isLoading
    ? t('common:processImportCsvLoadingTitle')
    : t('common:processImportCsvSuccessTitle');

  const loadingImportMultiInviteMessage = isLoading
    ? t('common:processImportMultiInviteLoadingTitle')
    : t('common:processImportMultiInviteSuccessTitle');

  const loadingImportOfflineSaleMessage = isLoading
    ? t('common:processMultiOfflineSalesLoadingTitle')
    : t('common:processMultiOfflineSalesSuccessTitle');

  const loadingImportPassHolderMessage = isLoading
    ? t('common:processImportPassHolderLoadingTitle')
    : t('common:processImportPassHolderSuccessTitle');

  const processesLiterals = {
    importCsv: {
      quantityFailied: failedRowsMessage,
      title: loadingImportCsvMessage,
    },
    invitation: {
      quantityFailied: failedRowsMultiInviteMessage,
      title: loadingImportMultiInviteMessage,
    },
    offlineSale: {
      quantityFailied: failedRowsOfflineSaleMessage,
      title: loadingImportOfflineSaleMessage,
    },
    passHolder: {
      quantityFailied: failedRowsPassHolderMessage,
      title: loadingImportPassHolderMessage,
    },
  };

  const sortedRowErrors =
    importCsvStatusData?.failed &&
    importCsvStatusData?.failed?.quantity > 0 &&
    importCsvStatusData?.failed?.rows.sort((a, b) => a - b);

  const percentageCompleted =
    importCsvStatusData?.pending === 0
      ? 100
      : calculatePercentage(importCsvStatusData?.completed ?? 0, totalRowsProcessed);

  useEffect(() => {
    setIsLoading(!!importCsvStatusData?.pending && importCsvStatusData?.pending > 0 && !!importId);
  }, [importCsvStatusData?.pending, importId]);

  useEffect(() => {
    if (importCsvStatusData && importCsvStatusData?.pending === 0) {
      setIsLoading(false);
    }
  }, [importCsvStatusData, importCsvStatusData?.pending]);

  useEffect(() => setLayout({ type: LAYOUT_WITHOUT_SIDEBAR }), [setLayout]);

  return (
    <div className='flex flex-col items-center justify-center w-full gap-8 px-4 sm:px-8 min-h-screen overflow-auto'>
      <div className='flex flex-col items-center justify-center max-w-[415px] mx-auto gap-8 pt-8 sm:mx-8'>
        <header>
          {isLoading ? (
            <IconLoading className='w-9 h-9 text-center mx-auto mb-6 animate-spin' />
          ) : (
            <IconCheckCircle className='w-9 h-9 text-center text-success-no-bg mx-auto mb-6' />
          )}
          <h1 className='text-headline-l md:text-headline-xl text-center mb-4 text-primary'>
            {processesLiterals[importType as keyof typeof processesLiterals].title}
          </h1>
          <p className='text-center text-secondary'>
            {isLoading ? (
              <Trans i18nKey='common:processImportInfoLoading' values={{ name: eventData?.name }}>
                <br />
                <span className='text-subtitle-m text-primary'>{eventData?.name}</span>
                <br />
              </Trans>
            ) : (
              <Trans
                i18nKey='common:processImportInfoFinished'
                values={{
                  name: eventData?.name,
                  extraLines: processesLiterals[importType as keyof typeof processesLiterals]?.quantityFailied,
                }}>
                We have imported the bookings into the event
                <span className='text-subtitle-m text-primary' />
                <span />
              </Trans>
            )}
          </p>
        </header>
        <section className=' w-full'>
          {importCsvStatusData && (
            <>
              <div className='flex justify-between'>
                <span className='text-subtitle-m text-primary'>
                  {t('eventDetails:bookingCount', {
                    value: `${importCsvStatusData?.completed} / ${totalRowsProcessed}`,
                  })}
                </span>
                <span className='text-tertiary'>
                  {calculatePercentage(importCsvStatusData?.completed ?? 0, totalRowsProcessed).toFixed(0)}%
                </span>
              </div>
              <ProgressLine percentageProgress={percentageCompleted} heightClassName='h-1' borderRadiusClassName='' />
            </>
          )}
        </section>
        {sortedRowErrors && (
          <section className='w-full -mt-4'>
            <CardDisclosure
              titleText={t('common:processImportCsvTitleErrors', { rows: importCsvStatusData?.failed?.quantity })}
              descriptionText={`${t('common:rows')}: ${createRowErrors(sortedRowErrors)}`}
              showChevronIcon
              variant={CRITICAL}
              isDefaultOpen={sortedRowErrors.length < 30}
            />
          </section>
        )}
        <footer className='flex items-center flex-col'>
          <ButtonSecondary onClick={navigateBack} className='mb-4 '>
            {t('common:doneButton')}
          </ButtonSecondary>
          {isLoading && <p className='text-center text-tertiary text-body-xs]'>{t('common:processImportInfoText')}</p>}
        </footer>
      </div>
    </div>
  );
};

export default ImportProcess;
